import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { FaqSection } from '../components/SEO';
import { ORGANIZATION_LD } from '../components/SEO';
import { PageHero } from '../components/PageShell';
import { PageShell } from '../components/PageShell';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { buildFaqLd } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const ProShopPage = () => {
  const { t } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const seo = t.seo.proShop;
  const faqs = t.proShopPage.faqs || [];
  const heroImg = 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/ru4ttb39_DSC06257.jpg';
  const galleryImg = 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/bzufvz83_DSC06265.jpg';
  const brands = [
    { name: 'Head', desc: t.proShopPage.headDesc },
    { name: 'Babolat', desc: t.proShopPage.babolatDesc },
    { name: 'Nox', desc: t.proShopPage.noxDesc }
  ];
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'SportingGoodsStore',
      '@id': `${SITE_URL}/pro-shop#store`,
      name: 'Padelarena Pro Shop',
      url: `${SITE_URL}/pro-shop`,
      image: heroImg,
      description: seo.description,
      address: ORGANIZATION_LD.address,
      telephone: '+41 43 833 0834',
      brand: ['Head', 'Babolat', 'Nox', 'Padlr'],
      makesOffer: [
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Padel rackets', brand: 'Head' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Padel rackets', brand: 'Babolat' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Padel rackets', brand: 'Nox' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Padel apparel', brand: 'Padlr' } }
      ],
      parentOrganization: { '@id': `${SITE_URL}/#organization` }
    },
    faqs.length ? buildFaqLd(faqs) : null
  ].filter(Boolean);

  return (
    <PageShell testId="proshop-page">
      <SEO path="/pro-shop" title={seo.title} description={seo.description} keywords={seo.keywords} image={heroImg} jsonLd={jsonLd} />
      <PageHero
        label={t.proShopPage.label}
        title={t.proShopPage.titleStart}
        accent={t.proShopPage.titleAccent}
        subtitle={t.proShopPage.subtitle}
        image={heroImg}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.proShopPage.racketsTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-6">{t.proShopPage.racketsIntro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {brands.map((b) => (
                <div key={b.name} className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                  <p className="text-2xl font-black text-slate-900 mb-1">{b.name}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Shop photo gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="proshop-gallery">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-100">
              <img src={heroImg} alt="Padel rackets wall at Padelarena Pro Shop" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-100">
              <img src={galleryImg} alt="Padel rackets display on-court at Padelarena" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.proShopPage.clothingTitle}</h2>
            <p className="text-slate-700 leading-relaxed">{t.proShopPage.clothingDesc}</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 md:p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.proShopPage.visitTitle}</h3>
            <p className="text-slate-700 mb-4">{t.proShopPage.visitDesc}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="tel:+41438330834" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-slate-900 text-white hover:bg-blue-600 transition-colors">
                <Phone className="w-4 h-4" /> +41 43 833 0834
              </a>
              <Link to="/#contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 border border-slate-200 transition-colors">
                {t.proShopPage.contactUs}
              </Link>
            </div>
          </div>

          {faqs.length > 0 && (
            <FaqSection title={t.proShopPage.faqTitle} faqs={faqs} />
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Featured upcoming event card (data-driven, supports multiple events)

export default ProShopPage;
